import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { finalize, timeout } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
interface Capteur {
  id: number;
  numero_serie: string;
  statut_capteur_id: number;
  date_emballage: Date;
  reference_envoi?: string | null;
  code_bon_retour?: string | null;
  code_bon_retour_cartouches?: string | null;
}

interface StatutCapteur {
  id: number;
  nom: string;
}

interface SuiviMessageResponse {
  message: string;
  returnCode: number;
  trackingNumber: string;
}

type SuiviCodeKey = 'reference_envoi' | 'code_bon_retour' | 'code_bon_retour_cartouches';



@Component({
  selector: 'app-colis-suivi',
  templateUrl: './colis-suivi.page.html',
  styleUrls: ['./colis-suivi.page.scss'],
  imports: [DatePipe]
})
export class ColisSuiviPage implements OnInit {
  capteurs: Capteur[] = [];
  statuts: StatutCapteur[] = [];
  isLoading = false;
  error: string | null = null;
  suiviLoadingByCapteur: Record<number, Partial<Record<SuiviCodeKey, boolean>>> = {};
  suiviMessageByCapteur: Record<number, Partial<Record<SuiviCodeKey, string>>> = {};
  suiviErrorByCapteur: Record<number, Partial<Record<SuiviCodeKey, string>>> = {};

  constructor(private http: HttpClient, private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.fetchCapteurs();
  }

  fetchCapteurs() {
    this.isLoading = true;
    // Récupère d'abord tous les statuts
    this.http.get<StatutCapteur[]>( `${environment.apiBaseUrl}/api/statut_capteurs`).subscribe({
      next: (statuts) => {
        this.statuts = statuts;
        // Cherche l'id du statut "en cours de transit"
        const transitStatut = statuts.find(s => s.nom === 'en cours de transit');
        if (!transitStatut) {
          this.error = 'Statut "en cours de transit" introuvable';
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }
        // Récupère tous les capteurs
        this.http.get<any>(`${environment.apiBaseUrl}/api/capteurs`).subscribe({
          next: (data) => {
            let capteursArr: Capteur[] = [];
            if (Array.isArray(data)) {
              capteursArr = data;
            } else if (data && Array.isArray(data.items)) {
              capteursArr = data.items;
            } else {
              this.error = 'Réponse inattendue du backend (capteurs non listés)';
              this.isLoading = false;
              this.cdr.detectChanges();
              return;
            }
            this.capteurs = capteursArr.filter(c => c.statut_capteur_id === transitStatut.id);
            this.isLoading = false;
            this.cdr.detectChanges();
          },
          error: (err) => {
            this.error = err.error?.detail || 'Erreur lors du chargement des capteurs.';
            this.isLoading = false;
            this.cdr.detectChanges();
          }
        });
      },
      error: (err) => {
        this.error = err.error?.detail || 'Erreur lors du chargement des statuts.';
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  fetchSuiviMessages(capteur: Capteur): void {
    this.fetchSuiviMessageForKey(capteur, 'reference_envoi');
    this.fetchSuiviMessageForKey(capteur, 'code_bon_retour');
    this.fetchSuiviMessageForKey(capteur, 'code_bon_retour_cartouches');
  }

  getSuiviMessage(capteurId: number, key: SuiviCodeKey): string {
    return this.suiviMessageByCapteur[capteurId]?.[key] || '-';
  }

  getSuiviError(capteurId: number, key: SuiviCodeKey): string {
    return this.suiviErrorByCapteur[capteurId]?.[key] || '';
  }

  isSuiviLoading(capteurId: number, key: SuiviCodeKey): boolean {
    return this.suiviLoadingByCapteur[capteurId]?.[key] === true;
  }

  private fetchSuiviMessageForKey(capteur: Capteur, key: SuiviCodeKey): void {
    const numeroSuivi = (capteur[key] ?? '').trim();
    this.ensureCapteurTrackingState(capteur.id);
    this.suiviErrorByCapteur[capteur.id][key] = '';

    if (!numeroSuivi) {
      this.suiviMessageByCapteur[capteur.id][key] = '';
      this.suiviErrorByCapteur[capteur.id][key] = 'Aucun numero.';
      this.cdr.detectChanges();
      return;
    }

    this.suiviLoadingByCapteur[capteur.id][key] = true;
    this.cdr.detectChanges();

    this.http
      .get<SuiviMessageResponse>( `${environment.apiBaseUrl}/api/suivi-colis/${encodeURIComponent(numeroSuivi)}`)
      .pipe(
        timeout(30000),
        finalize(() => {
          this.suiviLoadingByCapteur[capteur.id][key] = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          this.suiviMessageByCapteur[capteur.id][key] = response.message ?? '';
          this.suiviErrorByCapteur[capteur.id][key] = '';
        },
        error: (err) => {
          this.suiviMessageByCapteur[capteur.id][key] = '';
          if (err?.name === 'TimeoutError') {
            this.suiviErrorByCapteur[capteur.id][key] = 'Timeout du suivi (15s).';
            return;
          }
          this.suiviErrorByCapteur[capteur.id][key] = err?.error?.detail || 'Erreur suivi.';
        },
      });
  }

  private ensureCapteurTrackingState(capteurId: number): void {
    this.suiviLoadingByCapteur[capteurId] ??= {};
    this.suiviMessageByCapteur[capteurId] ??= {};
    this.suiviErrorByCapteur[capteurId] ??= {};
  }
}
